// A small, deterministic markdown → React renderer. core.md §9.4.
//
// No dependency, on purpose: the model's output is untrusted text inside the
// student's browser, and the whole surface this renderer exposes has to be
// readable in one file. What it understands is exactly what the teaching
// assistant produces — headings (rendered h3-h5, never h1/h2), paragraphs, bold/italic, inline
// code, fenced code (with a copy control), ordered/unordered lists nested one
// level, blockquotes, tables (inside `overflow-x: auto`) and horizontal rules.
//
// ── Two rules that are safety, not style ────────────────────────────────
//
//   1. RAW HTML IS ESCAPED, NEVER RENDERED. Every text run goes into a React
//      text node; nothing here uses `dangerouslySetInnerHTML`, so `<script>` in
//      an answer is the six characters `<script>` on screen.
//   2. LINKS RENDER AS TEXT. `[label](url)` becomes the label — no anchor, no
//      navigation from a model. A bare URL is likewise plain text.
//
// RTL prose with embedded Latin drug names sets `unicode-bidi: plaintext` on
// paragraphs, so «متفورمین 500 mg» keeps its digits and units in reading order.
//
// Two exports: `parseMarkdown(text)` → an AST (what the tests inspect) and
// `renderMarkdown(text, options)` → React nodes. `markdownToPlainText` is what
// the «کپی» action puts on the clipboard.

import React from 'react';

// ── Block parsing ──────────────────────────────────────────────────────────

const FENCE_RE = /^\s*```\s*([\w-]*)\s*$/;
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const HR_RE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE_RE = /^\s{0,3}>\s?(.*)$/;
const UL_RE = /^(\s*)[-*+]\s+(.*)$/;
const OL_RE = /^(\s*)(\d{1,3})[.)]\s+(.*)$/;
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const splitRow = (line) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

/** Parse the text into a list of block nodes. */
export function parseMarkdown(text) {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  const para = [];
  const flushPara = () => {
    if (!para.length) return;
    blocks.push({ type: 'paragraph', inlines: parseInline(para.join(' ')) });
    para.length = 0;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { flushPara(); i += 1; continue; }

    const fence = FENCE_RE.exec(line);
    if (fence) {
      flushPara();
      const lang = fence[1] || '';
      const body = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1; // the closing fence (or EOF)
      blocks.push({ type: 'code', lang, code: body.join('\n') });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      flushPara();
      // core.md §9.4: headings are «≤ h3» — an answer must never outrank the
      // page it sits in. The chat page owns the h1/h2 (the patient's name and
      // the thread title), so a model that opens with `# ارزیابی` must not
      // produce a second h1: to a screen reader the answer would read as a new
      // document, and the page outline would have two top-level headings.
      //
      // The three markdown tiers are therefore SHIFTED, not flattened —
      // `#`→h3, `##`→h4, `###`+→h5 — which keeps the relative hierarchy the
      // model expressed while starting below the page's own. Flattening every
      // level to h3 would have lost the structure of a long teaching answer.
      blocks.push({
        type: 'heading',
        level: Math.min(heading[1].length, 3) + 2,
        inlines: parseInline(heading[2]),
      });
      i += 1;
      continue;
    }

    if (HR_RE.test(line)) { flushPara(); blocks.push({ type: 'hr' }); i += 1; continue; }

    if (QUOTE_RE.test(line)) {
      flushPara();
      const inner = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) { inner.push(QUOTE_RE.exec(lines[i])[1]); i += 1; }
      blocks.push({ type: 'blockquote', children: parseMarkdown(inner.join('\n')) });
      continue;
    }

    if (TABLE_ROW_RE.test(line) && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1])) {
      flushPara();
      const header = splitRow(line).map(parseInline);
      i += 2;
      const rows = [];
      while (i < lines.length && TABLE_ROW_RE.test(lines[i])) { rows.push(splitRow(lines[i]).map(parseInline)); i += 1; }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (UL_RE.test(line) || OL_RE.test(line)) {
      flushPara();
      const { list, next } = parseList(lines, i, 0);
      blocks.push(list);
      i = next;
      continue;
    }

    para.push(line.trim());
    i += 1;
  }
  flushPara();
  return blocks;
}

/**
 * A list starting at `start`, nested ONE level: an item indented deeper than the
 * list's own indent opens a child list; anything deeper than that is folded into
 * the child (a third level is rare in an answer and not worth the recursion).
 */
function parseList(lines, start, depth) {
  const first = UL_RE.exec(lines[start]) || OL_RE.exec(lines[start]);
  const ordered = Boolean(OL_RE.exec(lines[start]));
  const indent = first[1].length;
  const items = [];
  let i = start;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) break;
    const ul = UL_RE.exec(line);
    const ol = OL_RE.exec(line);
    const m = ul || ol;
    if (!m) {
      // A continuation line of the previous item.
      if (items.length && /^\s+/.test(line)) { items[items.length - 1].inlines.push(...parseInline(` ${line.trim()}`)); i += 1; continue; }
      break;
    }
    const lineIndent = m[1].length;
    if (lineIndent < indent) break;
    if (lineIndent > indent && items.length) {
      if (depth >= 1) {
        // Deeper than we nest: keep the text, drop the structure.
        items[items.length - 1].inlines.push(...parseInline(` ${(ul ? ul[2] : ol[3]).trim()}`));
        i += 1;
        continue;
      }
      const { list, next } = parseList(lines, i, depth + 1);
      items[items.length - 1].children = list;
      i = next;
      continue;
    }
    items.push({ inlines: parseInline(ul ? ul[2] : ol[3]), children: null });
    i += 1;
  }
  return { list: { type: 'list', ordered, items }, next: i };
}

// ── Inline parsing ─────────────────────────────────────────────────────────

const INLINE_RE = /(`+)([\s\S]*?[^`])\1(?!`)|\*\*(.+?)\*\*|__(.+?)__|\*([^*\n]+?)\*|_([^_\n]+?)_|\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;

/** Text with bold / italic / code / links → inline nodes. */
export function parseInline(text) {
  const src = String(text ?? '');
  const out = [];
  let last = 0;
  let m;
  // A FRESH regex per call, not the shared `INLINE_RE`. This function recurses
  // (a `**bold**` run is parsed again for what is inside it), and a `/g` regex
  // carries its `lastIndex` on the OBJECT: the inner call reset the shared one
  // to 0, so on returning, the outer loop re-matched the same run forever and
  // `parseInline('**b**')` never terminated — it exhausted the heap. Every
  // answer a model writes contains bold, so this hung the page.
  const re = new RegExp(INLINE_RE.source, 'g');
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(src))) {
    if (m.index > last) out.push({ type: 'text', text: src.slice(last, m.index) });
    if (m[2] != null) out.push({ type: 'code', text: m[2] });
    else if (m[3] != null) out.push({ type: 'strong', children: parseInline(m[3]) });
    else if (m[4] != null) out.push({ type: 'strong', children: parseInline(m[4]) });
    else if (m[5] != null) out.push({ type: 'em', children: parseInline(m[5]) });
    else if (m[6] != null) out.push({ type: 'em', children: parseInline(m[6]) });
    // A link is its LABEL. The url is kept on the node for tests, not rendered.
    else if (m[7] != null) out.push({ type: 'link', text: m[7], url: m[8] });
    last = m.index + m[0].length;
    // A zero-length match cannot advance `lastIndex` on its own.
    if (m[0].length === 0) re.lastIndex += 1;
  }
  if (last < src.length) out.push({ type: 'text', text: src.slice(last) });
  return out;
}

// ── Plain text (for the clipboard) ────────────────────────────────────────

const inlineText = (nodes) =>
  (nodes || []).map((n) => {
    if (n.type === 'text' || n.type === 'code' || n.type === 'link') return n.text;
    return inlineText(n.children);
  }).join('');

export function markdownToPlainText(text) {
  const walk = (blocks) => blocks.map((b) => {
    switch (b.type) {
      case 'heading':
      case 'paragraph': return inlineText(b.inlines);
      case 'code': return b.code;
      case 'hr': return '———';
      case 'blockquote': return walk(b.children).join('\n');
      case 'table': return [b.header, ...b.rows].map((r) => r.map(inlineText).join(' | ')).join('\n');
      case 'list': return b.items.map((it, idx) =>
        `${b.ordered ? `${idx + 1}.` : '•'} ${inlineText(it.inlines)}${it.children ? `\n${walk([it.children]).join('\n').replace(/^/gm, '  ')}` : ''}`).join('\n');
      default: return '';
    }
  });
  return walk(parseMarkdown(text)).join('\n\n');
}

// ── React rendering ────────────────────────────────────────────────────────

const h = React.createElement;

const STYLE = {
  p: { margin: '0 0 0.75em', lineHeight: 2, unicodeBidi: 'plaintext', overflowWrap: 'anywhere' },
  // Ranks h3–h5, never h1/h2 — see the heading branch in parseMarkdown. The
  // SIZES are unchanged from the three tiers this renderer always drew; only
  // the semantic rank moved down.
  h3: { margin: '1em 0 0.4em', fontSize: '1.125em', fontWeight: 800, lineHeight: 1.6 },
  h4: { margin: '1em 0 0.4em', fontSize: '1.0625em', fontWeight: 800, lineHeight: 1.6 },
  h5: { margin: '0.9em 0 0.35em', fontSize: '1em', fontWeight: 800, lineHeight: 1.6 },
  code: { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.875em', padding: '0.1em 0.35em', borderRadius: 6, background: 'rgba(0,0,0,0.06)', direction: 'ltr', unicodeBidi: 'isolate' },
  pre: { margin: 0, padding: '0.75rem 0.875rem', overflowX: 'auto', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.8125em', lineHeight: 1.7, direction: 'ltr', textAlign: 'left', whiteSpace: 'pre' },
  preWrap: { position: 'relative', margin: '0 0 0.75em', borderRadius: 10, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' },
  copy: { position: 'absolute', top: 6, insetInlineEnd: 6, fontSize: '0.75em', padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  ul: { margin: '0 0 0.75em', paddingInlineStart: '1.4em', lineHeight: 1.95 },
  li: { margin: '0.15em 0', unicodeBidi: 'plaintext' },
  blockquote: { margin: '0 0 0.75em', paddingInlineStart: '0.875em', borderInlineStart: '3px solid rgba(0,0,0,0.12)', opacity: 0.9 },
  tableWrap: { overflowX: 'auto', margin: '0 0 0.75em' },
  table: { borderCollapse: 'collapse', fontSize: '0.9em', minWidth: '100%' },
  th: { textAlign: 'start', padding: '6px 10px', borderBottom: '2px solid rgba(0,0,0,0.12)', fontWeight: 800, whiteSpace: 'nowrap' },
  td: { padding: '6px 10px', borderBottom: '1px solid rgba(0,0,0,0.08)', verticalAlign: 'top' },
  hr: { border: 'none', borderTop: '1px solid rgba(0,0,0,0.12)', margin: '1em 0' },
};

function renderInlines(nodes, keyPrefix) {
  return (nodes || []).map((n, idx) => {
    const key = `${keyPrefix}-${idx}`;
    switch (n.type) {
      case 'text': return n.text;
      case 'code': return h('code', { key, style: STYLE.code }, n.text);
      case 'strong': return h('strong', { key }, renderInlines(n.children, key));
      case 'em': return h('em', { key }, renderInlines(n.children, key));
      // No anchor: a model's link is text (core.md §9.4).
      case 'link': return h('span', { key, 'data-md-link': '' }, n.text);
      default: return null;
    }
  });
}

/** The fenced-code block, with its copy control. Its own component so it may hold state. */
function CodeBlock({ code, lang, labels, onCopy }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    onCopy?.(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return h('div', { style: STYLE.preWrap, 'data-md-code': lang || '' },
    labels?.copy
      ? h('button', { type: 'button', style: STYLE.copy, onClick: copy, 'aria-label': labels.copy }, copied ? (labels.copied || labels.copy) : labels.copy)
      : null,
    h('pre', { style: STYLE.pre }, h('code', null, code)),
  );
}

function renderBlocks(blocks, keyPrefix, options) {
  return blocks.map((b, idx) => {
    const key = `${keyPrefix}-${idx}`;
    switch (b.type) {
      case 'paragraph': return h('p', { key, style: STYLE.p }, renderInlines(b.inlines, key));
      case 'heading': {
        const tag = `h${b.level}`;
        return h(tag, { key, style: STYLE[tag] }, renderInlines(b.inlines, key));
      }
      case 'code': return h(CodeBlock, { key, code: b.code, lang: b.lang, labels: options.labels, onCopy: options.onCopy });
      case 'hr': return h('hr', { key, style: STYLE.hr });
      case 'blockquote': return h('blockquote', { key, style: STYLE.blockquote }, renderBlocks(b.children, key, options));
      case 'list': return h(b.ordered ? 'ol' : 'ul', { key, style: STYLE.ul },
        b.items.map((it, i) => h('li', { key: `${key}-${i}`, style: STYLE.li },
          renderInlines(it.inlines, `${key}-${i}`),
          it.children ? renderBlocks([it.children], `${key}-${i}-c`, options) : null)));
      case 'table': return h('div', { key, style: STYLE.tableWrap, 'data-md-table': '' },
        h('table', { style: STYLE.table },
          h('thead', null, h('tr', null, b.header.map((c, i) => h('th', { key: i, style: STYLE.th }, renderInlines(c, `${key}-h${i}`))))),
          h('tbody', null, b.rows.map((r, ri) => h('tr', { key: ri },
            r.map((c, ci) => h('td', { key: ci, style: STYLE.td }, renderInlines(c, `${key}-r${ri}c${ci}`))))))));
      default: return null;
    }
  });
}

/**
 * Markdown → React nodes.
 *
 * @param options.labels  `{ copy, copied }` for the code-block control (i18n is
 *                        the caller's; this module carries no catalogue)
 * @param options.onCopy  (code) => void — the clipboard write is the caller's too
 */
export function renderMarkdown(text, options = {}) {
  return renderBlocks(parseMarkdown(text), options.keyPrefix || 'md', options);
}

/** A ready component for the common case. */
export function Markdown({ text, labels, onCopy, style, className, ...rest }) {
  const nodes = React.useMemo(() => renderMarkdown(text, { labels, onCopy }), [text, labels, onCopy]);
  return h('div', { className, style: { fontSize: '0.9375rem', ...style }, 'data-md': '', ...rest }, nodes);
}

export default renderMarkdown;
